import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ArrowRight, Zap } from "lucide-react";
import { api } from "../../lib/api";

/**
 * SignupHeroBanner — the in-your-face top banner that hits logged-out
 * users between the eyes the moment they land.
 *
 * Why aggressive: the conversion ladder is
 *    land on site  →  see signup CTA  →  sign up  →  book.
 * Burying the signup behind a small "Log in" link in the header was
 * leaking conversions. This banner sits ABOVE the launch countdown,
 * full-width orange with a pulse animation, and links to /register
 * with `next=` set to wherever the user was browsing — so after
 * signup they pop right back to the same page.
 *
 * Hidden for: signed-in users, the /register page itself, the /login
 * page (already a signup CTA there), and admin/share marketing pages
 * where it'd interfere with the screen-record workflow.
 */
const HIDE_ON = ["/register", "/login", "/share/", "/admin", "/print/", "/billing"];

export default function SignupHeroBanner() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [count, setCount] = useState(null);

  // Pull live count from the same endpoint the Landing + Header use, so
  // the number on the banner never falls behind reality after ingest.
  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && data?.total) setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading || user) return null;
  if (HIDE_ON.some((p) => location.pathname === p || location.pathname.startsWith(p))) return null;

  // Round DOWN to the nearest 500 for a clean marketing number that
  // never overstates the catalogue.
  const tagCount = count ? `${(Math.floor(count / 500) * 500).toLocaleString("en-GB")}+` : "22,000+";

  const next = `${location.pathname}${location.search}`;
  const safeNext = next.startsWith("/") && !next.startsWith("//") ? next : "/browse";

  return (
    <Link
      to={`/register?next=${encodeURIComponent(safeNext)}`}
      className="block relative overflow-hidden group focus:outline-none focus:ring-4 focus:ring-[#ff4500]/40"
      data-testid="signup-hero-banner"
      style={{
        background: "linear-gradient(90deg, #ff4500 0%, #ff6a1f 50%, #ff4500 100%)",
        backgroundSize: "200% 100%",
        animation: "signupHeroSlide 6s linear infinite",
      }}
    >
      {/* Animated diagonal stripes pattern for extra attention-grab */}
      <span
        aria-hidden="true"
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 12px, transparent 12px 24px)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-3 sm:px-6 py-3 sm:py-3.5 flex items-center justify-between gap-3 text-white">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 sm:w-9 sm:h-9 bg-white text-[#ff4500] flex-shrink-0 animate-pulse">
            <Zap className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} fill="#ff4500" />
          </span>
          <div className="min-w-0">
            <div className="font-display text-base sm:text-xl leading-tight uppercase tracking-wide drop-shadow">
              Sign up free · 20 seconds
            </div>
            <div className="text-[10px] sm:text-xs mono uppercase tracking-wider text-white/90 truncate">
              {tagCount} UK campsites · Save favourites · Book in one tap
            </div>
          </div>
        </div>
        <span
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 bg-white text-[#ff4500] font-display text-sm sm:text-base uppercase tracking-wider shadow-[0_3px_0_0_#0e0e0e] group-hover:shadow-[0_1px_0_0_#0e0e0e] group-hover:translate-y-[2px] transition-all"
          data-testid="signup-hero-cta"
        >
          Sign up <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>

      <style>{`
        @keyframes signupHeroSlide {
          0%   { background-position:   0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
    </Link>
  );
}
